import { Component } from '@theme/component';

/**
 * Family spotlight tabs and pager (APG tabs pattern).
 *
 * The section server-renders every panel visible and stacked, with every page
 * of products inside each panel, so the whole family is readable without
 * JavaScript. This component collapses that to one visible panel showing one
 * page and owns the state from then on: roving tabindex, `aria-selected`, the
 * `hidden` attribute on inactive panels and pages, and the per-panel page
 * counter with its bound-disabled buttons.
 *
 * Paging is purely client-side over already-rendered markup — no network.
 *
 * The untabbed fallback (a single panel, no tablist) uses the same machinery
 * with one panel named `default`, so `tabs` and the pager refs are optional.
 *
 * @typedef {object} FamilySpotlightRefs
 * @property {HTMLButtonElement[]} [tabs] - The tab buttons, in DOM order.
 * @property {HTMLElement[]} panels - The tab panels, in DOM order.
 * @property {HTMLElement[]} pages - Every page of every panel, in DOM order.
 * @property {HTMLElement[]} [pagers] - The per-panel pager groups in the controls row.
 * @property {HTMLElement[]} [counters] - The "current / total" readouts.
 * @property {HTMLButtonElement[]} [previousButtons] - Previous-page buttons.
 * @property {HTMLButtonElement[]} [nextButtons] - Next-page buttons.
 *
 * @extends {Component<FamilySpotlightRefs>}
 */
export class FamilySpotlightComponent extends Component {
  requiredRefs = ['panels', 'pages'];

  /**
   * The `data-tab` value of the selected panel, or null before the first sync.
   *
   * @type {string | null}
   */
  #selected = null;

  /**
   * The current page number per `data-tab` value. Panels keep their page while
   * another tab is showing.
   *
   * @type {Map<string, number>}
   */
  #pages = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  /**
   * Re-applies selection after a Section Rendering API re-render, which
   * restores the server-rendered state (first tab active, nothing hidden).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  /**
   * Selects the previously selected panel when it still exists, otherwise the
   * first one, dropping page state for panels that are gone.
   */
  #sync() {
    const names = this.refs.panels?.map((panel) => panel.dataset.tab ?? '') ?? [];

    if (!names.length) return;

    for (const name of this.#pages.keys()) {
      if (!names.includes(name)) this.#pages.delete(name);
    }

    this.#select(names.includes(this.#selected ?? '') ? this.#selected : (names[0] ?? null));
  }

  /**
   * Activates one panel and its tab, hiding the others.
   *
   * @param {string | null} name - The `data-tab` value to activate.
   * @param {object} [options]
   * @param {boolean} [options.focus=false] - Move focus to the activated tab.
   * @param {boolean} [options.resetPage=false] - Return the panel to page 1.
   *   Passed by user-initiated tab changes (GLOSS-72 round 2 QA: switching
   *   tabs always lands on the first page); the connect/re-render sync keeps
   *   whatever page the panel was on.
   */
  #select(name, { focus = false, resetPage = false } = {}) {
    if (!name) return;

    const { tabs, panels } = this.refs;

    this.#selected = name;

    if (resetPage) this.#pages.set(name, 1);

    for (const tab of tabs ?? []) {
      const active = tab.dataset.tab === name;

      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;

      if (active && focus) tab.focus();
    }

    for (const panel of panels) {
      panel.hidden = panel.dataset.tab !== name;
    }

    // The controls row holds one pager per panel; only the active panel's shows.
    for (const pager of this.refs.pagers ?? []) {
      pager.hidden = pager.dataset.tab !== name;
    }

    this.#renderPages();
  }

  /**
   * @param {string} name - A `data-tab` value.
   * @returns {number} How many pages that panel server-rendered.
   */
  #pageCount(name) {
    return this.refs.pages.filter((page) => page.dataset.tab === name).length;
  }

  /**
   * @param {string} name - A `data-tab` value.
   * @returns {number} That panel's current page, clamped to what exists.
   */
  #currentPage(name) {
    const total = this.#pageCount(name);
    const current = this.#pages.get(name) ?? 1;

    return Math.min(Math.max(current, 1), Math.max(total, 1));
  }

  /**
   * Shows the current page of every panel and syncs the pager controls.
   */
  #renderPages() {
    const { pages, counters, previousButtons, nextButtons } = this.refs;

    for (const page of pages) {
      const name = page.dataset.tab ?? '';

      page.hidden = Number(page.dataset.page) !== this.#currentPage(name);
    }

    for (const counter of counters ?? []) {
      const name = counter.dataset.tab ?? '';
      const text = `${this.#currentPage(name)} / ${this.#pageCount(name)}`;

      // The readout is a live region: only write when it actually changes, so
      // the first sync (which matches what the server rendered) stays silent.
      if (counter.textContent !== text) counter.textContent = text;
    }

    for (const button of previousButtons ?? []) {
      button.disabled = this.#currentPage(button.dataset.tab ?? '') <= 1;
    }

    for (const button of nextButtons ?? []) {
      const name = button.dataset.tab ?? '';

      button.disabled = this.#currentPage(name) >= this.#pageCount(name);
    }
  }

  /**
   * Moves one panel through its own pages, staying within bounds.
   *
   * @param {string | undefined} name - The panel's `data-tab` value.
   * @param {number} delta - -1 for the previous page, 1 for the next.
   */
  #turnPage(name, delta) {
    if (!name) return;

    const next = this.#currentPage(name) + delta;

    if (next < 1 || next > this.#pageCount(name)) return;

    this.#pages.set(name, next);
    this.#renderPages();
  }

  /**
   * Shows the previous page of the button's panel.
   *
   * @param {MouseEvent} event - The click event, retargeted to the button.
   */
  previousPage = (event) => {
    const button = /** @type {HTMLElement} */ (event.target);

    this.#turnPage(button.dataset.tab, -1);
  };

  /**
   * Shows the next page of the button's panel.
   *
   * @param {MouseEvent} event - The click event, retargeted to the button.
   */
  nextPage = (event) => {
    const button = /** @type {HTMLElement} */ (event.target);

    this.#turnPage(button.dataset.tab, 1);
  };

  /**
   * Activates the clicked tab.
   *
   * @param {MouseEvent} event - The click event, retargeted to the tab button.
   */
  selectTab = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target);

    this.#select(tab.dataset.tab ?? null, { resetPage: true });
  };

  /**
   * Roving-tabindex keyboard navigation: ArrowLeft/ArrowRight (direction-aware),
   * Home, and End move selection and focus together.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeyDown = (event) => {
    const tabs = this.refs.tabs ?? [];
    const last = tabs.length - 1;

    if (last < 0) return;

    const current = Math.max(
      0,
      tabs.findIndex((tab) => tab.dataset.tab === this.#selected)
    );
    const forward = getComputedStyle(this).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = forward === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';

    let next = current;

    switch (event.key) {
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      case forward:
        next = current === last ? 0 : current + 1;
        break;
      case backward:
        next = current === 0 ? last : current - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    this.#select(tabs[next]?.dataset.tab ?? null, { focus: true, resetPage: true });
  };
}

if (!customElements.get('family-spotlight-component')) {
  customElements.define('family-spotlight-component', FamilySpotlightComponent);
}
